import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { 
  Banknote, 
  Download, 
  Calendar, 
  Play, 
  Eye, 
  CheckCircle, 
  ChevronDown,
  Search,
  Users,
  DollarSign,
  Clock,
  XCircle,
  Loader2,
  FileSpreadsheet,
  FileText,
  File
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Payroll() {
  const [pendingEmployees, setPendingEmployees] = useState([]);
  const [confirmedPayrolls, setConfirmedPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('unconfirmed');
  
  // Date filters
  const [selectedMonth, setSelectedMonth] = useState('today');
  const [customDate, setCustomDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [calculatedPayroll, setCalculatedPayroll] = useState(null);
  const [processMonth, setProcessMonth] = useState(new Date().getMonth() + 1);
  const [processYear, setProcessYear] = useState(new Date().getFullYear());
  const [processing, setProcessing] = useState(false);
  const [confirming, setConfirming] = useState({});
  const [calculating, setCalculating] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('hrms_user') || '{}');
  const canProcess = ['admin', 'payroll_officer', 'super_admin', 'hr_manager'].includes(user.role);

  // Generate month options with "Today" as first option
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    
    // Add "Today" option
    options.push({
      value: 'today',
      label: `Today (${now.toLocaleDateString('en-MY', { month: 'short', year: 'numeric' })})`
    });
    
    // Add past months
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })
      });
    }
    return options;
  };
  
  const monthOptions = getMonthOptions();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('hrToken');
    return { Authorization: `Bearer ${token}` };
  };

  // Get the effective date for API calls
  const getEffectiveDate = () => {
    if (selectedMonth === 'today') {
      return new Date().toISOString().split('T')[0];
    }
    return `${selectedMonth}-01`;
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, customDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const dateStr = customDate || getEffectiveDate();
      
      // For confirmed, use the exact date to filter
      const [year, month, day] = dateStr.split('-').map(Number);
      
      // Fetch pending and confirmed payrolls
      const [pendingRes, confirmedRes] = await Promise.all([
        axios.get(`${API}/payroll/pending?date=${dateStr}`, { headers }),
        // Get confirmed for the specific date
        axios.get(`${API}/payroll/confirmed?date=${dateStr}`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setPendingEmployees(pendingRes.data || []);
      
      // Filter confirmed by exact date if custom date is selected
      let confirmed = confirmedRes.data || [];
      if (customDate) {
        confirmed = confirmed.filter(p => p.date === customDate);
      }
      setConfirmedPayrolls(confirmed);
    } catch (error) {
      console.error('Payroll fetch error:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to fetch payroll data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayroll = async () => {
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/payroll/process?month=${processMonth}&year=${processYear}`, {}, { headers });
      toast.success('Payroll processed successfully!');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payroll');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewEmployee = async (employee) => {
    setSelectedEmployee(employee);
    setCalculatedPayroll(null);
    setViewDialogOpen(true);
    
    setCalculating(true);
    try {
      const headers = getAuthHeaders();
      const dateStr = customDate || getEffectiveDate();
      
      const response = await axios.post(`${API}/payroll/calculate`, {
        employee_id: employee.employee_id,
        date: dateStr
      }, { headers });
      
      setCalculatedPayroll(response.data);
    } catch (error) {
      console.error('Calculate payroll error:', error);
      setCalculatedPayroll({
        basic_salary: employee.salary_basic || employee.salary || 0,
        allowances: employee.salary_allowances || employee.allowances || 0,
        claims_reimbursement: 0,
        epf_employee: 0,
        socso_employee: 0,
        eis_employee: 0,
        pcb_tax: 0,
        total_deductions: 0,
        gross_salary: employee.salary_basic || employee.salary || 0,
        net_salary: employee.salary_basic || employee.salary || 0
      });
    } finally {
      setCalculating(false);
    }
  };

  const handleConfirmPayroll = async (employee) => {
    if (!calculatedPayroll) {
      toast.error('Please view employee details first');
      return;
    }
    
    setConfirming(prev => ({ ...prev, [employee.employee_id]: true }));
    try {
      const headers = getAuthHeaders();
      const dateStr = customDate || new Date().toISOString().split('T')[0];
      
      const confirmData = {
        employee_id: employee.employee_id,
        employee_name: employee.full_name || employee.name,
        date: dateStr,
        basic_salary: calculatedPayroll.basic_salary || 0,
        allowances: calculatedPayroll.allowances || 0,
        claims_reimbursement: calculatedPayroll.claims_reimbursement || 0,
        approved_claims_count: calculatedPayroll.approved_claims_count || 0,
        epf_employee: calculatedPayroll.epf_employee || 0,
        epf_employer: calculatedPayroll.epf_employer || 0,
        socso_employee: calculatedPayroll.socso_employee || 0,
        socso_employer: calculatedPayroll.socso_employer || 0,
        eis_employee: calculatedPayroll.eis_employee || 0,
        eis_employer: calculatedPayroll.eis_employer || 0,
        pcb_tax: calculatedPayroll.pcb_tax || 0,
        total_deductions: calculatedPayroll.total_deductions || 0,
        gross_salary: calculatedPayroll.gross_salary || 0,
        net_salary: calculatedPayroll.net_salary || 0,
        department: employee.department,
        position: employee.position
      };
      
      await axios.post(`${API}/payroll/confirm`, confirmData, { headers });
      toast.success(`Payroll confirmed for ${employee.full_name || employee.name || employee.employee_id}`);
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payroll');
    } finally {
      setConfirming(prev => ({ ...prev, [employee.employee_id]: false }));
    }
  };

  const handleUnconfirmPayroll = async (payrollId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/payroll/confirmed/${payrollId}`, { headers });
      toast.success('Payroll unconfirmed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unconfirm payroll');
    }
  };

  // Download functionality
  const downloadPayroll = (format) => {
    if (confirmedPayrolls.length === 0) {
      toast.error('No confirmed payrolls to download');
      return;
    }

    const dateStr = customDate || new Date().toISOString().split('T')[0];
    const filename = `payroll_${dateStr}`;

    if (format === 'csv') {
      downloadCSV(filename);
    } else if (format === 'excel') {
      downloadExcel(filename);
    } else if (format === 'txt') {
      downloadTXT(filename);
    }
  };

  const downloadCSV = (filename) => {
    const headers = ['Employee ID', 'Employee Name', 'Department', 'Position', 'Basic Salary', 'Allowances', 'Gross Salary', 'EPF Employee', 'SOCSO', 'EIS', 'PCB Tax', 'Total Deductions', 'Net Salary', 'Date'];
    
    const rows = confirmedPayrolls.map(p => [
      p.employee_id,
      p.employee_name,
      p.department || '',
      p.position || '',
      p.basic_salary?.toFixed(2) || '0.00',
      p.allowances?.toFixed(2) || '0.00',
      p.gross_salary?.toFixed(2) || '0.00',
      p.epf_employee?.toFixed(2) || '0.00',
      p.socso_employee?.toFixed(2) || '0.00',
      p.eis_employee?.toFixed(2) || '0.00',
      p.pcb_tax?.toFixed(2) || '0.00',
      p.total_deductions?.toFixed(2) || '0.00',
      p.net_salary?.toFixed(2) || '0.00',
      p.date
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
    toast.success(`Downloaded ${confirmedPayrolls.length} records as CSV`);
  };

  const downloadExcel = (filename) => {
    // For Excel, we'll create a CSV that Excel can open
    // In production, you'd use a library like xlsx
    downloadCSV(filename);
    toast.info('Excel file downloaded (CSV format compatible with Excel)');
  };

  const downloadTXT = (filename) => {
    let content = `PAYROLL REPORT - ${customDate || new Date().toISOString().split('T')[0]}\n`;
    content += `${'='.repeat(80)}\n\n`;
    content += `Total Records: ${confirmedPayrolls.length}\n`;
    content += `Total Net Payroll: RM ${confirmedPayrolls.reduce((sum, p) => sum + (p.net_salary || 0), 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}\n\n`;
    content += `${'='.repeat(80)}\n\n`;

    confirmedPayrolls.forEach((p, idx) => {
      content += `${idx + 1}. ${p.employee_name} (${p.employee_id})\n`;
      content += `   Department: ${p.department || '-'} | Position: ${p.position || '-'}\n`;
      content += `   Basic: RM ${p.basic_salary?.toFixed(2)} | Gross: RM ${p.gross_salary?.toFixed(2)} | Net: RM ${p.net_salary?.toFixed(2)}\n`;
      content += `   Deductions - EPF: RM ${p.epf_employee?.toFixed(2)} | SOCSO: RM ${p.socso_employee?.toFixed(2)} | EIS: RM ${p.eis_employee?.toFixed(2)} | PCB: RM ${p.pcb_tax?.toFixed(2)}\n\n`;
    });

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.txt`;
    link.click();
    toast.success(`Downloaded ${confirmedPayrolls.length} records as TXT`);
  };

  // Filter by search
  const filterBySearch = (items, isConfirmed = false) => {
    if (!searchTerm) return items;
    const search = searchTerm.toLowerCase();
    return items.filter(item => {
      const name = isConfirmed ? item.employee_name : (item.full_name || item.name);
      const empId = item.employee_id;
      const dept = item.department;
      const pos = item.position;
      return (
        empId?.toLowerCase().includes(search) ||
        name?.toLowerCase().includes(search) ||
        dept?.toLowerCase().includes(search) ||
        pos?.toLowerCase().includes(search)
      );
    });
  };

  const filteredPending = filterBySearch(pendingEmployees, false);
  const filteredConfirmed = filterBySearch(confirmedPayrolls, true);

  // Stats
  const totalPending = pendingEmployees.length;
  const totalConfirmed = confirmedPayrolls.length;
  const totalPayroll = confirmedPayrolls.reduce((sum, p) => sum + (p.net_salary || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-slate-900 min-h-screen p-6 -m-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Payroll Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Process monthly payroll for employees
          </p>
        </div>
        {canProcess && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="process-payroll-btn"
              >
                <Play className="h-4 w-4 mr-2" />
                Process Payroll
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Process Monthly Payroll</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Month</Label>
                  <select
                    value={processMonth}
                    onChange={(e) => setProcessMonth(parseInt(e.target.value))}
                    className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1, 1).toLocaleDateString('en-MY', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <select
                    value={processYear}
                    onChange={(e) => setProcessYear(parseInt(e.target.value))}
                    className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm"
                  >
                    {[2024, 2025, 2026, 2027].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProcessPayroll}
                    disabled={processing}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {processing ? 'Processing...' : 'Process Payroll'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-900/30 flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Employees</p>
              <p className="text-xl font-bold text-white">{totalPending + totalConfirmed}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Confirmed (Today)</p>
              <p className="text-xl font-bold text-white">{totalConfirmed}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-900/30 flex items-center justify-center">
              <Clock className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Pending</p>
              <p className="text-xl font-bold text-white">{totalPending}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-900/30 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Today's Net Payroll</p>
              <p className="text-xl font-bold text-white">
                RM {totalPayroll.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Date Filters - MyHR Orbit Style */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
        <div className="flex flex-wrap items-center gap-6">
          {/* Quick Select Month */}
          <div className="flex flex-col gap-2">
            <Label className="text-sm text-slate-400">
              Quick Select Month
            </Label>
            <div className="relative">
              <select
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value === 'today') {
                    setCustomDate(new Date().toISOString().split('T')[0]);
                  }
                }}
                className="h-12 pl-4 pr-10 rounded-xl border border-slate-600 bg-slate-700 text-white text-sm appearance-none cursor-pointer min-w-[200px] font-medium"
              >
                {monthOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Or Custom Date */}
          <div className="flex flex-col gap-2">
            <Label className="text-sm text-slate-400">
              Or Custom Date
            </Label>
            <div className="relative">
              <Input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                className="h-12 pl-4 pr-10 rounded-xl border border-slate-600 bg-slate-700 text-white text-sm min-w-[180px] font-medium"
              />
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px] flex flex-col gap-2">
            <Label className="text-sm text-slate-400">
              Search
            </Label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name, ID, department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-12 pl-10 rounded-xl border-slate-600 bg-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Download Button - Only show when on Confirmed tab and has data */}
          {activeTab === 'confirmed' && confirmedPayrolls.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label className="text-sm text-slate-400">
                Export
              </Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="h-12 px-6 bg-green-600 hover:bg-green-700 text-white rounded-xl">
                    <Download className="h-4 w-4 mr-2" />
                    Download ({confirmedPayrolls.length})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-slate-800 border-slate-700">
                  <DropdownMenuItem onClick={() => downloadPayroll('csv')} className="text-white hover:bg-slate-700">
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-400" />
                    Download CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadPayroll('excel')} className="text-white hover:bg-slate-700">
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-400" />
                    Download Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadPayroll('txt')} className="text-white hover:bg-slate-700">
                    <FileText className="h-4 w-4 mr-2 text-blue-400" />
                    Download TXT
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('unconfirmed')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === 'unconfirmed'
              ? 'bg-slate-700 text-white shadow-sm'
              : 'text-slate-400 hover:text-white'
          }`}
          data-testid="tab-unconfirmed"
        >
          Unconfirmed ({totalPending})
        </button>
        <button
          onClick={() => setActiveTab('confirmed')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === 'confirmed'
              ? 'bg-slate-700 text-white shadow-sm'
              : 'text-slate-400 hover:text-white'
          }`}
          data-testid="tab-confirmed"
        >
          Confirmed ({totalConfirmed})
        </button>
      </div>

      {/* Employee Payroll Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-slate-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Employee ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Position
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Basic Salary
                </th>
                {activeTab === 'confirmed' && (
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Net Salary
                  </th>
                )}
                {activeTab === 'unconfirmed' && (
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Claims
                  </th>
                )}
                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {activeTab === 'unconfirmed' ? (
                filteredPending.length > 0 ? (
                  filteredPending.map((employee) => (
                    <tr 
                      key={employee.employee_id} 
                      className="hover:bg-slate-700/50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-white">
                          {employee.employee_id}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
                            {(employee.full_name || employee.name)?.charAt(0) || 'E'}
                          </div>
                          <span className="text-sm font-medium text-white">
                            {employee.full_name || employee.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-300">
                          {employee.department || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-300">
                          {employee.position || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-medium text-white tabular-nums">
                          RM {(employee.salary_basic || employee.salary || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm text-slate-300 tabular-nums">
                          RM 0.00
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewEmployee(employee)}
                            className="h-8 px-3 text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                          {canProcess && (
                            <Button
                              size="sm"
                              onClick={() => handleViewEmployee(employee)}
                              className="h-8 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle className="h-3.5 w-3.5 mr-1" />
                              Confirm
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
                      <p className="text-slate-400">
                        All payrolls have been confirmed for this period
                      </p>
                    </td>
                  </tr>
                )
              ) : (
                filteredConfirmed.length > 0 ? (
                  filteredConfirmed.map((payroll) => (
                    <tr 
                      key={payroll.payroll_id} 
                      className="hover:bg-slate-700/50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-white">
                          {payroll.employee_id}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white text-xs font-medium">
                            {payroll.employee_name?.charAt(0) || 'E'}
                          </div>
                          <span className="text-sm font-medium text-white">
                            {payroll.employee_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-300">
                          {payroll.department || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-300">
                          {payroll.position || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-medium text-white tabular-nums">
                          RM {(payroll.basic_salary || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-green-400 tabular-nums">
                          RM {(payroll.net_salary || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedEmployee(payroll);
                              setCalculatedPayroll(payroll);
                              setViewDialogOpen(true);
                            }}
                            className="h-8 px-3 text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                          {canProcess && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnconfirmPayroll(payroll.payroll_id)}
                              className="h-8 px-3 text-xs text-red-400 hover:text-red-300 border-red-800 hover:border-red-700 hover:bg-red-900/20"
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" />
                              Unconfirm
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Banknote className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">
                        No confirmed payrolls for {customDate ? new Date(customDate).toLocaleDateString('en-MY', { dateStyle: 'long' }) : 'this date'}
                      </p>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View/Confirm Employee Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {activeTab === 'unconfirmed' ? 'Confirm Employee Payroll' : 'Payroll Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-lg font-medium">
                  {(selectedEmployee.full_name || selectedEmployee.name || selectedEmployee.employee_name)?.charAt(0) || 'E'}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">
                    {selectedEmployee.full_name || selectedEmployee.name || selectedEmployee.employee_name}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {selectedEmployee.employee_id} • {selectedEmployee.position || '-'}
                  </p>
                </div>
              </div>
              
              {calculating ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                </div>
              ) : calculatedPayroll && (
                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Basic Salary</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      RM {(calculatedPayroll.basic_salary || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Allowances</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      RM {(calculatedPayroll.allowances || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-2">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-slate-900 dark:text-white">Gross Salary</span>
                      <span className="text-slate-900 dark:text-white">
                        RM {(calculatedPayroll.gross_salary || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-2 space-y-2">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Deductions</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">EPF (Employee)</span>
                      <span className="text-red-600">- RM {(calculatedPayroll.epf_employee || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">SOCSO</span>
                      <span className="text-red-600">- RM {(calculatedPayroll.socso_employee || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">EIS</span>
                      <span className="text-red-600">- RM {(calculatedPayroll.eis_employee || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">PCB Tax</span>
                      <span className="text-red-600">- RM {(calculatedPayroll.pcb_tax || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-slate-900 dark:text-white">Net Salary</span>
                      <span className="font-bold text-lg text-green-600">
                        RM {(calculatedPayroll.net_salary || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
                {activeTab === 'unconfirmed' && canProcess && calculatedPayroll && (
                  <Button 
                    onClick={() => handleConfirmPayroll(selectedEmployee)}
                    disabled={confirming[selectedEmployee.employee_id]}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {confirming[selectedEmployee.employee_id] ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Confirming...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Confirm Payroll
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
